import { Button } from '@mui/material';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import Message from '/@/components/message';
import { _ } from '/@/locale';
import { range } from '/@/utils';

import styles from './styles.module.css';

@autobind
export default class BarChart extends Component {
    static propTypes = {
        data: PropTypes.arrayOf(
            PropTypes.shape({
                head: PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    name: PropTypes.string.isRequired,
                }).isRequired,
                other: PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    name: PropTypes.string.isRequired,
                }).isRequired,
                value: PropTypes.number.isRequired,
                result: PropTypes.oneOf(['win', 'lose', 'tie']).isRequired,
                trialCount: PropTypes.number,
                winRate: PropTypes.number,
            })
        ).isRequired,
        renderValue: PropTypes.func.isRequired,
        onLinearRegressionClick: PropTypes.func,
        user: PropTypes.shape({
            email: PropTypes.string.isRequired,
        }).isRequired,
        showWinRate: PropTypes.bool,
    };

    static defaultProps = {
        showWinRate: false,
    };

    render() {
        const { data, renderValue, user, onLinearRegressionClick } = this.props;
        const maxValue = Math.max(...data.map(({ value }) => Math.abs(value)));
        if (data.length === 0) {
            return (
                <Message type='warning' appearance='fill' showIcon>
                    {_('No hay datos disponibles para los filtros seleccionados.')}
                </Message>
            );
        }

        return (
            <div className={`${styles.root} mp-mask`}>
                {data.map((datum) => {
                    const bar = datum.value !== 0 && (
                        <div
                            className={classNames(styles.bar, styles[datum.result])}
                            style={{ width: `${Math.abs(datum.value / maxValue) * 100}%` }}
                        />
                    );
                    const value = <div className={styles.value}>{renderValue(datum.value)}</div>;
                    return (
                        <BarChartRow
                            datum={datum}
                            onLinearRegressionClick={onLinearRegressionClick}
                            user={user}
                            bar={bar}
                            value={value}
                            showWinRate={this.props.showWinRate}
                            key={datum.id}
                        />
                    );
                })}
            </div>
        );
    }
}

const BarChartRow = ({ datum, onLinearRegressionClick, user, bar, value, showWinRate }) => {
    const renderMetadata = (datum) => {
        const hasWinRate = !Number.isNaN(Number(datum.winRate));
        const hasTrialCount = Boolean(datum.trialCount);
        if (!hasTrialCount && !hasWinRate) return null;

        return (
            <div className={styles.metadata}>
                {hasTrialCount && hasWinRate && (
                    <div>
                        {_('Comparaciones')}: {datum.trialCount}
                    </div>
                )}
                {hasWinRate && showWinRate && (
                    <div>
                        {_('Win rate')}: {Math.round(datum.winRate)}%
                    </div>
                )}
            </div>
        );
    };

    return (
        <Fragment>
            <div className={styles.header}>
                <div className={styles.product}>
                    <div className={styles.productName}>{datum.other.name}</div>
                    {renderMetadata(datum)}
                </div>
                {onLinearRegressionClick && datum.linearRegressionEnabled && (
                    <Button
                        sx={{ mx: { md: 1 } }}
                        variant='outlined'
                        color='gray'
                        size='small'
                        onClick={() => onLinearRegressionClick(datum.other)}
                    >
                        {_('Regresión lineal')}
                    </Button>
                )}
            </div>
            <div className={styles.barContainer}>
                <div className={styles.left}>{datum.value < 0 ? bar : value}</div>
                <div className={styles.right}>{datum.value >= 0 ? bar : value}</div>
                <div className={styles.watermark}>{range(10).map(() => `${user.email} `)}</div>
            </div>
        </Fragment>
    );
};
